import type {
  BankAccountResponse,
  LinkBankAccountProps,
  Mutate,
} from '@iheartjane/data-access';
import { trackError } from '@iheartjane/tracking';
import type { AerosyncWidget, WidgetEventSuccessType } from 'aerosync-web-sdk';
import { AerosyncEnvironment, initAeroSyncWidget } from 'aerosync-web-sdk';
import { useEffect, useRef, useState } from 'react';

interface UseAerosyncProps {
  /** Aerosync consumer id from app config */
  aeropayConsumerId: string;

  /** Aerosync environment string from app config — `'production'` switches to the production widget, anything else uses sandbox */
  aeropayEnv: string;

  /** Function called when the user exits the widget */
  onClose?(): void;

  /** Function called when bank account fails to link */
  onError(err: unknown): void;

  /** Mutation called to persist the linked bank account on aeropay */
  onLinkBankAccount: Mutate<LinkBankAccountProps, BankAccountResponse>;

  /** Function called when the widget finishes loading */
  onLoad?(): void;

  /** Function called when the bank account is successfully linked */
  onSuccess?(): void;

  /** Aerosync widget token, fetched by the consumer (e.g. via `useAeropayBankConnection`) */
  token?: string | null;

  /** Widget container id */
  widgetContainerId: string;
}

/** @see https://dev.aero.inc/v1.1/docs/integration-guide */
export const useAerosync = ({
  aeropayConsumerId,
  aeropayEnv,
  onClose,
  onError,
  onLinkBankAccount,
  onLoad,
  onSuccess,
  token,
  widgetContainerId,
}: UseAerosyncProps) => {
  const shouldClose = useRef(true);
  const widgetRef = useRef<AerosyncWidget | null>(null);
  const [linkingBankAccount, setLinkingBankAccount] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // The aerosync widget invokes its own onClose function when successfully linking a bank account.
  // We pass onClose the close modal function for when a user hits the X button.
  // However, we do not want to close the modal upon linking a bank account.
  const handleClose = () => {
    if (shouldClose.current) {
      onClose && onClose();
    }
  };

  useEffect(() => {
    if (token) {
      const shadowRoot = document.querySelector('#shadow-host')?.shadowRoot;

      const environment: AerosyncEnvironment =
        aeropayEnv === 'production'
          ? AerosyncEnvironment.Production
          : AerosyncEnvironment.Sandbox;

      const sharedProps = {
        token: token,
        elementId: widgetContainerId,
        iframeTitle: 'Connect',
        style: {
          width: '100%',
          height: '100%',
        },
        environment,
        consumerId: aeropayConsumerId,
        onEvent: function () {
          return null;
        },
        onLoad: function () {
          onLoad && onLoad();
        },
        onSuccess: function (e: WidgetEventSuccessType) {
          shouldClose.current = false;
          const event = e;
          setLinkingBankAccount(true);
          onLinkBankAccount(
            {
              aeropayUserId: event.user_id,
              aeropayUserPassword: event.user_password,
            },
            {
              onSuccess: (response) => {
                setLinkingBankAccount(false);
                if (response.success) {
                  onSuccess && onSuccess();
                } else {
                  onError(response.error);
                }
              },
              onError: (err) => {
                setLinkingBankAccount(false);
                onError(err);
              },
            }
          );
        },
        onClose: handleClose,
        onError: (event: string) => {
          trackError(`Aerosync Error`, { error: event });
          onError && onError(event);
        },
      };

      const ref = initAeroSyncWidget({
        ...sharedProps,
        targetDocument: shadowRoot || undefined,
      });

      widgetRef.current = ref;
      setIsLoading(false);

      return () => {
        ref.destroy();
      };
    } else {
      return undefined;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, widgetContainerId]);

  return { isLoading, linkingBankAccount, widgetRef };
};
